<template>
    <div class="device-controls__block">
        <div class="device-controls__row device-controls__row--title">
            <span class="dci__title">
                <span class="sg-vrm-icon_113_inverter-24px"></span>
                {{ formattedName }}
            </span>
        </div>
        <div
            v-if="changeModeTimer.nextChange !== null"
            :style="{ height: bodyHeight + 'px' }"
            class="device-controls__body"
        >
            <change-warning
                @cancel="changeModeTimer.clearChangeTimer()"
                @confirm="changeModeTimer.confirmChange()"
            >
                <div
                    v-html="
                        $t('dashboard.dci.countdown_timer', {
                            nextState: $t(
                                `dashboard.dci.ac_system.radio.${getRadioValue(
                                    changeModeTimer.nextChange,
                                )}`,
                            ),
                            seconds: timerLimit - changeModeTimer.changeSeconds,
                        })
                    "
                ></div>
            </change-warning>
        </div>
        <div
            v-else-if="changeLimitTimer.nextChange !== null"
            :style="{ height: bodyHeight + 'px' }"
            class="device-controls__body"
        >
            <change-warning @cancel="cancelLimitChange" @confirm="changeLimitTimer.confirmChange()">
                <div
                    v-html="
                        $t('dashboard.dci.countdown_timer_current', {
                            nextState: changeLimitTimer.nextChange.value + ' A',
                            seconds: timerLimit - changeLimitTimer.changeSeconds,
                        })
                    "
                ></div>
            </change-warning>
        </div>
        <div
            v-else-if="!needsConfirmation"
            ref="acSystemBody"
            class="device-controls__body device-controls__body--inverter"
        >
            <div v-for="acInputIdx in currentAcInputsIndexes" :key="acInputIdx">
                <div class="device-controls__row">
                    <div>
                        <span
                            class="dci__action"
                            :class="{ 'dci__action--disabled': isWaitingForResponse }"
                        >
                            {{ $t('dashboard.dci.not_available_current_limit') }}
                        </span>
                    </div>
                    <input
                        v-model="getAcLimitsFormatted()[acInputIdx]"
                        type="number"
                        class="sg-element"
                        min="0"
                        :error="acLimitChange.error"
                        :disabled="!acLimitAdjustable[acInputIdx] || isWaitingForResponse"
                        @input="debounceChangeLimit(acInputIdx, acLimitsFormatted[acInputIdx])"
                    />
                    <span class="unit">A</span>
                    <span v-if="acLimitChange.error" class="dci__warning dci__warning--inline">
                        {{ $t('dashboard.dci.errors.' + acLimitChange.error) }}
                    </span>
                </div>
                <div v-if="suggestedAmperage" class="device-controls__row">
                    <button
                        v-for="amperage in suggestedAmperage"
                        :key="amperage"
                        class="dci__inline-btn"
                        :class="{
                            'dci__inline-btn--active': amperage === acLimits[acInputIdx],
                        }"
                        :disabled="!acLimitAdjustable[acInputIdx] || isWaitingForResponse"
                        @click="changeLimitWithTimer(acInputIdx, amperage)"
                    >
                        {{ amperage }}
                    </button>
                </div>
                <div class="dci__border" />
            </div>
            <div class="device-controls__row">
                <h4
                    class="dci__subtitle"
                    :class="{ 'dci__subtitle--disabled': isWaitingForResponse }"
                >
                    {{ $t(`dashboard.dci.mode`, { i18nFallback: 'Mode' }) }}
                </h4>
                <span v-if="inverterModeChange.error" class="dci__warning dci__warning--inline">
                    {{ $t('dashboard.dci.errors.' + inverterModeChange.error) }}
                </span>
            </div>
            <div
                v-for="radioValue in radioValues"
                :key="radioValue.valueEnum"
                class="device-controls__row device-controls__row--checkbox "
                :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
                @click="
                    () => {
                        if (!isWaitingForResponse && inverterMode !== radioValue.valueEnum)
                            changeModeWithTimer(radioValue.valueEnum);
                    }
                "
            >
                <span class="dci__action">
                    {{
                        $t(`dashboard.dci.ac_system.radio.${radioValue.nameEnum}`, {
                            i18nFallback: radioValue.nameEnum,
                        })
                    }}
                </span>
                <div class="vrm-form__custom-radio-checkbox ">
                    <div class="sg-custom-check-and-radio custom-radio">
                        <label>
                            <input
                                type="radio"
                                :checked="inverterMode === radioValue.valueEnum"
                                :class="{ 'sg-disabled': isWaitingForResponse }"
                            />
                            <div class="sg-alternative-display">
                                <div class="sg-outer">
                                    <div class="sg-inner"></div>
                                </div>
                            </div>
                        </label>
                    </div>
                </div>
            </div>
        </div>
        <div v-else :style="{ height: bodyHeight + 'px' }" class="device-controls__body">
            <div class="dci__warning">
                <div class="dci__warning__body">
                    <span
                        class="sg-vrm-icon_012_alarm-32px dci__warning__icon dci__warning__icon--red"
                    ></span>
                    <div
                        v-html="
                            $t('dashboard.stopping_warning', {
                                device: $t('dashboard.inverter').toLowerCase(),
                                i18nFallback:
                                    'Stopping the inverter could cause loss of power. This action cannot be reversed. <br /><br /> Are you sure you want to continue?',
                            })
                        "
                    ></div>
                </div>
                <button class="sg-btn sg-element sg-grey" @click="cancelStopping()">
                    {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                </button>
                <button class="sg-btn sg-element sg-red" @click="stopWithConfirmation()">
                    {{ $t('dashboard.inverter_stop', { i18nFallback: 'Stop inverter' }) }}
                </button>
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { ngInject } from '@/vue/decorators/angular-interop';
import { createTimer } from './components/Timer';

import {
    getSuggestedAmperageValuesList,
    getAcSystemSubscriptions,
    subscribeToInfo,
    unsubscribeFromInfo,
    publishToPath,
} from './utils';

import { newMqttWriteValue, initiateValueLoading, resolveValue } from './writeValueUtils';
import Spinner from './components/Spinner';
import ChangeWarning from '@/vue/components/shared/device-controls/controls/components/ChangeWarning';

@Component({
    components: {
        Spinner,
        ChangeWarning,
    },
})
@AngularLocaleBundle('dashboard')
@AngularLocaleBundle('common')
export default class AcSystem extends Vue {
    @ngInject()
    MQTTRealtimeService;
    @ngInject()
    ApiService;

    @Prop({ type: Number, required: true })
    instance;
    @Prop({ type: Number, required: true })
    installationId;
    @Prop({ type: String, required: true })
    identifier;
    @Prop({ type: Number, required: true })
    timerLimit;

    radioValues = [
        { nameEnum: 'On', valueEnum: 3 },
        { nameEnum: 'Charger Only', valueEnum: 1 },
        { nameEnum: 'Inverter Only', valueEnum: 2 },
        { nameEnum: 'Off', valueEnum: 4 },
        { nameEnum: 'Passthrough', valueEnum: 251 },
    ];

    SUBSCRIPTIONS = getAcSystemSubscriptions();

    // controlled by mqtt
    inverterMode = null;

    acLimits = [null, null];
    acLimitAdjustable = [false, false];
    productId = null;
    customName = null;
    productName = null;

    // not controlled by mqtt
    acInputs = [1]; // Unlike Inverter, only 1 possible input for AC System (for now)

    // inherited from createTimer at mount
    changeModeTimer = {};
    changeLimitTimer = {};
    selectedInverterMode = null;

    debouncedChangeLimit = null;

    acLimitChange = newMqttWriteValue({ varName: 'acLimits' });
    inverterModeChange = newMqttWriteValue({ varName: 'inverterMode' });

    needsConfirmation = false;

    bodyHeight = 195 + this.currentAcInputsIndexes.length * 145;

    created() {
        createTimer.call(this, 'changeModeTimer', this.timerLimit);
        createTimer.call(this, 'changeLimitTimer', this.timerLimit);
    }

    mounted() {
        this.subscribeToInfo = subscribeToInfo.bind(this);
        this.unsubscribeFromInfo = unsubscribeFromInfo.bind(this);
        this.publishToPath = publishToPath.bind(this);
        this.initiateValueLoading = initiateValueLoading.bind(this);
        this.resolveValue = resolveValue.bind(this);
        this.subscribeToInfo();
    }

    beforeDestroy() {
        window.clearTimeout(this.acLimitChange.timer);
        window.clearTimeout(this.inverterModeChange.timer);
        this.unsubscribeFromInfo();
    }

    @Watch('acLimits')
    onAcLimits() {
        this.resolveValue(this.acLimitChange, this.availableAcLimit);
    }

    @Watch('inverterMode')
    onInverterMode() {
        this.resolveValue(this.inverterModeChange, this.inverterMode);
    }

    get isWaitingForResponse() {
        return this.acLimitChange.busy || this.inverterModeChange.busy;
    }

    get currentAcInputsIndexes() {
        return this.acInputs
            .map((value, index) => (value ? index : null))
            .filter(idx => idx !== null && this.acLimitAdjustable[idx]);
    }

    changeModeWithTimer(value) {
        if (this.isWaitingForResponse) return;
        this.matchBodyHeight();
        if (value === 4) {
            this.stopWithConfirmation();
            return;
        }
        this.selectedInverterMode = value;
        this.changeModeTimer.callFuncWithTimer(value, this.changeMode);
    }

    changeMode(value) {
        this.inverterModeChange.oldState = this.inverterMode;
        this.inverterModeChange.newState = value;
        this.initiateValueLoading(this.inverterModeChange);

        this.publishToPath(
            {
                dbusPath: '/Mode',
                callbackIdentifier: `setModeOfInverter_${this.instance}`,
            },
            { value },
            this.inverterMode,
        );
    }

    stopWithConfirmation() {
        if (this.needsConfirmation) {
            this.needsConfirmation = false;
            this.changeMode(4);
        } else {
            this.needsConfirmation = true;
        }
    }

    cancelStopping() {
        this.needsConfirmation = false;
    }

    debounceChangeLimit(inputNr, value) {
        clearTimeout(this.debouncedChangeLimit);

        this.debouncedChangeLimit = setTimeout(() => {
            this.changeLimitWithTimer(inputNr, value);
        }, 1500);
    }

    changeLimitWithTimer(inputNr, value) {
        if (this.isWaitingForResponse || +value === this.acLimits[inputNr]) return;
        this.matchBodyHeight();
        this.changeLimitTimer.callFuncWithTimer({ inputNr, value }, this.changeLimit);
    }

    cancelLimitChange() {
        this.changeLimitTimer.clearChangeTimer();
        this.acLimits = this.acLimits.slice(); // trigger v-model to reset input field to old value
    }

    changeLimit({ inputNr, value }) {
        this.acLimitChange.oldState = this.availableAcLimit;
        this.acLimitChange.newState = +value;
        this.acLimitChange.varIdx = inputNr;
        this.initiateValueLoading(this.acLimitChange);

        this.publishToPath(
            {
                dbusPath: `/Ac/In/${inputNr + 1}/CurrentLimit`,
                callbackIdentifier: `setCurrentLimitOfInverter_${this.instance}_${inputNr}`,
            },
            { value },
            this.acLimits[inputNr],
        );
    }

    getRadioValue(value) {
        const foundValue = this.radioValues.find(radioValue => radioValue.valueEnum == value);

        if (foundValue) {
            return foundValue.nameEnum;
        }
        return null;
    }

    getCallbackIdentifier(varName) {
        return `get_${varName}OfInverter_${this.instance}`;
    }

    get formattedName() {
        if (this.customName) {
            return this.customName;
        }
        if (this.productName) {
            return this.productName;
        }
        return `AC System`;
    }

    get mqttPathObj() {
        return {
            identifier: this.identifier,
            dbusServiceType: 'acsystem',
            instance: this.instance,
        };
    }

    get acLimitsFormatted() {
        return this.acLimits.map(value =>
            typeof value == 'number' ? Number(value.toFixed(2)) : 0,
        );
    }

    getAcLimitsFormatted() {
        return this.acLimitsFormatted;
    }

    get suggestedAmperage() {
        return getSuggestedAmperageValuesList(this.productId);
    }

    get availableAcLimit() {
        return this.acLimits[0] != null ? this.acLimits[0] : this.acLimits[1];
    }

    matchBodyHeight() {
        if (this.$refs.acSystemBody !== undefined)
            this.bodyHeight = this.$refs.acSystemBody.clientHeight;
    }
}
</script>
